/**
 * Test Kitchen context capture.
 *
 * The whole point of the "Report a problem" button is that a dietary tester types one line and
 * nothing else — no reproduction steps, no F12, no error codes. That only works if the client
 * quietly collects everything a developer would otherwise have to ask for.
 *
 * Three ring buffers feed that: navigation/action breadcrumbs, failed API calls, and console
 * errors. All are bounded and in-memory only — nothing is persisted and nothing is sent anywhere
 * until a tester actually submits a finding.
 */

/** Keep these small: the payload rides along with a PNG and lands in a JSONB column. */
const MAX_BREADCRUMBS = 25;
const MAX_FAILED_CALLS = 10;
const MAX_CONSOLE_ERRORS = 10;

export interface Breadcrumb {
  at: string;
  kind: 'nav' | 'click' | 'action';
  detail: string;
}

export interface FailedCall {
  at: string;
  method: string;
  url: string;
  status?: number;
  message?: string;
}

export interface ConsoleError {
  at: string;
  message: string;
  stack?: string;
}

export interface FeedbackContext {
  breadcrumbs: Breadcrumb[];
  failedCalls: FailedCall[];
  consoleErrors: ConsoleError[];
  storeSnapshot?: Record<string, unknown>;
  capturedAt: string;
}

const breadcrumbs: Breadcrumb[] = [];
const failedCalls: FailedCall[] = [];
const consoleErrors: ConsoleError[] = [];

function push<T>(buffer: T[], item: T, max: number) {
  buffer.push(item);
  if (buffer.length > max) buffer.splice(0, buffer.length - max);
}

const now = () => new Date().toISOString();

/**
 * Records a navigation or user action. Breadcrumbs are what let us stop asking testers for
 * reproduction steps — the sequence is reconstructed from these instead.
 */
export function recordBreadcrumb(kind: Breadcrumb['kind'], detail: string) {
  push(breadcrumbs, { at: now(), kind, detail: detail.slice(0, 200) }, MAX_BREADCRUMBS);
}

export function recordFailedCall(call: Omit<FailedCall, 'at'>) {
  push(
    failedCalls,
    {
      at: now(),
      method: call.method,
      url: call.url.slice(0, 300),
      status: call.status,
      message: call.message?.slice(0, 300),
    },
    MAX_FAILED_CALLS,
  );
}

export function recordConsoleError(message: string, stack?: string) {
  push(
    consoleErrors,
    { at: now(), message: message.slice(0, 500), stack: stack?.slice(0, 2000) },
    MAX_CONSOLE_ERRORS,
  );
}

/**
 * Registers window-level error listeners.
 *
 * Deliberately does NOT monkey-patch console.error: React logs a great deal of noise there in
 * development, and burying the one real error in twenty warnings defeats the purpose. Genuine
 * uncaught errors and rejected promises are the signal worth keeping.
 */
export function installErrorCapture() {
  if (typeof window === 'undefined') return () => {};

  const onError = (event: ErrorEvent) => {
    recordConsoleError(event.message, event.error?.stack);
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    recordConsoleError(
      typeof reason === 'string' ? reason : (reason?.message ?? 'Unhandled promise rejection'),
      reason?.stack,
    );
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/**
 * Store snapshot providers. Registered lazily by whoever owns the state so this module stays
 * free of store imports — importing stores here would create a cycle through the API client.
 */
// Nullable return so a provider can say "nothing selected yet" (no connection, no facility)
// without having to invent an empty object.
type SnapshotProvider = () => Record<string, unknown> | null;
const snapshotProviders = new Map<string, SnapshotProvider>();

export function registerStoreSnapshot(key: string, provider: SnapshotProvider) {
  snapshotProviders.set(key, provider);
  return () => snapshotProviders.delete(key);
}

/** Keys whose values must never leave the browser, matched case-insensitively as substrings. */
const REDACT_PATTERNS = ['token', 'password', 'secret', 'apikey', 'api_key', 'authorization', 'credential'];

function redact(value: unknown, depth = 0): unknown {
  if (depth > 4) return '[depth limit]';
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.slice(0, 25).map((v) => redact(v, depth + 1));

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const lower = k.toLowerCase();
      out[k] = REDACT_PATTERNS.some((p) => lower.includes(p)) ? '[redacted]' : redact(v, depth + 1);
    }
    return out;
  }

  if (typeof value === 'string' && value.length > 500) return `${value.slice(0, 500)}…`;
  return value;
}

/**
 * Assembles everything captured so far. Snapshot providers are called defensively: a throwing
 * provider must not cost the tester their finding.
 */
export function collectFeedbackContext(): FeedbackContext {
  const storeSnapshot: Record<string, unknown> = {};

  for (const [key, provider] of snapshotProviders) {
    try {
      storeSnapshot[key] = redact(provider());
    } catch (err) {
      storeSnapshot[key] = `[snapshot failed: ${(err as Error)?.message ?? 'unknown'}]`;
    }
  }

  return {
    breadcrumbs: [...breadcrumbs],
    failedCalls: [...failedCalls],
    consoleErrors: [...consoleErrors],
    storeSnapshot: Object.keys(storeSnapshot).length > 0 ? storeSnapshot : undefined,
    capturedAt: now(),
  };
}

/** Browser/OS string for the finding. Not parsed anywhere — it's read by a human during triage. */
export function describeBrowser(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  return navigator.userAgent.slice(0, 400);
}

/**
 * Viewport plus enough signal to tell OS display scaling apart from browser zoom.
 *
 * devicePixelRatio alone is the PRODUCT of the two, which is not diagnosable: a finding reporting
 * "@1.55x" could be 125% Windows with 124% browser zoom, or 155% Windows at 100%, and the advice
 * differs completely. screen.width is reported in CSS pixels scaled by OS settings but NOT by
 * browser zoom, so the ratio between it and outerWidth isolates the zoom component.
 *
 * Reported as a compact single line because it lands in a grid column a human reads.
 */
export function describeViewport(): string {
  if (typeof window === 'undefined') return 'unknown';

  const dpr = window.devicePixelRatio ?? 1;
  const parts = [`${window.innerWidth}x${window.innerHeight}`, `dpr ${round2(dpr)}`];

  try {
    // Browser zoom shows up as outerWidth shrinking relative to the screen's CSS width.
    // Rounded to the nearest 5% — the raw ratio jitters by a pixel or two.
    const zoom = Math.round((window.outerWidth / window.screen.width) * 20) * 5;
    if (Number.isFinite(zoom) && zoom > 0) parts.push(`zoom ~${zoom}%`);
    parts.push(`screen ${window.screen.width}x${window.screen.height}`);
  } catch {
    // Some embedded contexts lock down `screen`; the viewport alone is still useful.
  }

  return parts.join(' · ').slice(0, 120);
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Test-only: clears every buffer so cases don't leak into each other. */
export function __resetFeedbackContext() {
  breadcrumbs.length = 0;
  failedCalls.length = 0;
  consoleErrors.length = 0;
  snapshotProviders.clear();
}
